import { useCallback, useMemo } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { apiUrl } from '../constants';

/**
 * Hook for making authenticated admin API calls.
 * Returns a stable object reference to avoid re-render loops in useEffect.
 */
export function useAdminApi() {
  const { getAuthHeaders, logout } = useAdminAuth();

  const adminFetch = useCallback(
    async (path: string, options: RequestInit = {}) => {
      const res = await fetch(apiUrl(path), {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
          ...options.headers,
        },
      });

      if (res.status === 401) {
        logout();
        throw new Error('Session expired');
      }

      return res;
    },
    [getAuthHeaders, logout]
  );

  const get = useCallback(
    async <T = any>(path: string): Promise<T> => {
      const res = await adminFetch(path);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      return data;
    },
    [adminFetch]
  );

  const post = useCallback(
    async <T = any>(path: string, body: any): Promise<T> => {
      const res = await adminFetch(path, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      return data;
    },
    [adminFetch]
  );

  const put = useCallback(
    async <T = any>(path: string, body: any): Promise<T> => {
      const res = await adminFetch(path, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      return data;
    },
    [adminFetch]
  );

  const del = useCallback(
    async <T = any>(path: string): Promise<T> => {
      const res = await adminFetch(path, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      return data;
    },
    [adminFetch]
  );

  // Return a stable reference — prevents useEffect infinite loops
  return useMemo(() => ({ get, post, put, del }), [get, post, put, del]);
}
